import { useId, useState } from 'react'
import { IconArrowRight, IconLock, IconShieldCheck, IconUser } from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom'

import { authApi } from '@/api/endpoints'
import { errorCode, errorMessage } from '@/api/client'
import { useAuthStore } from '@/store/authStore'
import { AuthFormBrand, AuthShell, LoginAside } from '@/components/auth/AuthShell'
import { tr } from '@/i18n/tr'
import { Alert, Button, PasswordField, TextField, useForm } from '@/ui'

/**
 * Giriş ekranı.
 *
 * <p>Solda markanın koyu yüzü ve uygulamanın ne yaptığını gösteren küçük bir
 * kompozisyon, sağda sakin bir form. Dar ekranda tanıtım gizlenir (CSS): giriş
 * yapmaya gelen kullanıcının önüne geçmesin.
 */
export function LoginPage() {
  const { user, setSession } = useAuthStore()
  const navigate = useNavigate()
  const location = useLocation() as { state?: { from?: string } }
  // Parola değişimi oturumu kapatınca AuthGate girişe "from: /parola-degistir" ile
  // yönlendirir; yeni parolayla girene o forma geri dönmek anlamsız (ve sonsuz döngü
  // hissi verir). Zorunlu değişim gerekiyorsa aşağıda mustChangePassword yönlendirir.
  const from = location.state?.from
  const target = from && from !== '/parola-degistir' ? from : '/'
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  // Parola doğru ama ikinci adım gerekiyorsa kod alanı açılır.
  const [needsCode, setNeedsCode] = useState(false)
  // Authenticator'a erişemeyen kullanıcı TOTP yerine kurtarma kodu girer.
  const [useRecovery, setUseRecovery] = useState(false)
  // "Parolamı unuttum" yalnız sunucu gerçekten posta gönderebiliyorsa görünür: e-posta
  // kapalıyken bağlantı, hiç gelmeyecek bir postayı bekletirdi.
  const options = useQuery({ queryKey: ['auth-options'], queryFn: authApi.options, staleTime: 60_000, retry: false })

  const form = useForm({
    initialValues: { username: '', password: '', totpCode: '' },
    validate: {
      username: (v) => (v.trim() ? null : tr.common.required),
      password: (v) => (v ? null : tr.common.required),
    },
  })

  // Zaten girişli kullanıcı giriş sayfasına gelirse (ya da başarılı giriş
  // setSession ile user'ı doldurduğunda) hedefe yönlendir. Bu muhafız, submit
  // içindeki imperatif navigate'ten ÖNCE yeniden render'da çalıştığı için
  // hedefi BURADA onurlandırmak gerekir; aksi halde korumalı derin bağlantıdan
  // (örn. /guvenlik) girişte kullanıcı hep "/"'a düşerdi (BUG-1).
  if (user) {
    return <Navigate to={user.mustChangePassword ? '/parola-degistir' : target} replace />
  }

  const submit = form.onSubmit(async (values) => {
    setBusy(true)
    setError(null)
    try {
      const res = await authApi.login(
        values.username.trim().toLowerCase(),
        values.password,
        values.totpCode || undefined,
      )
      setSession(res.accessToken, res.user)
      navigate(res.user.mustChangePassword ? '/parola-degistir' : target, {
        replace: true,
      })
    } catch (err) {
      if (errorCode(err) === 'TWO_FACTOR_REQUIRED') {
        setNeedsCode(true)
        setError(null)
      } else {
        setError(errorMessage(err, 'Kullanıcı adı ya da parola hatalı.'))
      }
    } finally {
      setBusy(false)
    }
  })

  const icon = { size: 18, stroke: 1.8, 'aria-hidden': true }
  return (
    <AuthShell asideLabel="Arsivora hakkında" aside={<LoginAside />}>
      <form className="nx-auth-form" onSubmit={submit} noValidate>
        <AuthFormBrand />
        <div className="nx-auth-form__head">
          <h1>Oturum açın</h1>
          <p>Kurum hesabınızla devam edin.</p>
        </div>

        {error && (
          <Alert tone="danger" icon={<IconAlertMark />}>
            <strong>Giriş yapılamadı.</strong> {error}
          </Alert>
        )}

        <TextField label={tr.auth.username} placeholder="kullanici.adi" size="lg" autoFocus
          autoComplete="username" autoCapitalize="none" spellCheck={false}
          leftSection={<IconUser {...icon} />} {...form.getInputProps('username')} />
        <PasswordField label={tr.auth.password} placeholder="••••••••" size="lg" autoComplete="current-password"
          leftSection={<IconLock {...icon} />} {...form.getInputProps('password')} />

        {needsCode && !useRecovery && (
          <>
            <OtpField value={form.values.totpCode} onChange={(v) => form.setFieldValue('totpCode', v)} />
            <button type="button" className="nx-auth-link"
              onClick={() => { setUseRecovery(true); form.setFieldValue('totpCode', '') }}>
              Telefonunuza erişemiyor musunuz? Kurtarma kodu kullanın
            </button>
          </>
        )}
        {needsCode && useRecovery && (
          <>
            <TextField label="Kurtarma kodu" placeholder="XXXXX-XXXXX" size="lg" autoFocus
              autoCapitalize="characters" autoComplete="one-time-code" spellCheck={false}
              leftSection={<IconShieldCheck {...icon} />}
              value={form.values.totpCode}
              onChange={(e) => form.setFieldValue('totpCode', e.currentTarget.value.toUpperCase().slice(0, 11))} />
            <button type="button" className="nx-auth-link"
              onClick={() => { setUseRecovery(false); form.setFieldValue('totpCode', '') }}>
              Doğrulama uygulaması koduna dön
            </button>
          </>
        )}

        <Button type="submit" size="lg" full loading={busy} className="nx-auth-submit"
          rightIcon={busy ? undefined : <IconArrowRight size={18} stroke={1.8} aria-hidden="true" />}>
          {busy ? tr.auth.signingIn : tr.auth.login}
        </Button>

        {options.data?.passwordReset && (
          <Link to="/parolami-unuttum" className="nx-auth-link">{tr.auth.forgotPassword}</Link>
        )}

        <p className="nx-auth-note">Hesabınız yoksa sistem yöneticinizle iletişime geçin.</p>
        <div className="nx-auth-foot">
          <IconLock size={14} stroke={1.8} aria-hidden="true" />
          Dosyalar kurum sunucusunda kalır.
        </div>
      </form>
    </AuthShell>
  )
}

function IconAlertMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
      <path d="M12 3a9 9 0 1 0 0 18a9 9 0 1 0 0-18M12 8v5M12 16.5h.01" />
    </svg>
  )
}

/**
 * Doğrulama kodu: altı hücre olarak görünen tek bir alan. Klavye, yapıştırma ve
 * parola yöneticilerinin "tek kullanımlık kod" doldurması tek alanda sorunsuz çalışır.
 */
function OtpField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const id = useId()
  const [focused, setFocused] = useState(false)
  const active = Math.min(value.length, 5)
  return (
    <fieldset className="nx-otp">
      <legend id={`${id}-l`}><IconShieldCheck size={16} stroke={1.8} aria-hidden="true" />Doğrulama kodu</legend>
      <span id={`${id}-h`} className="nx-otp__hint">Doğrulama uygulamanızdaki 6 haneli kod</span>
      <div className="nx-otp__cells">
        {Array.from({ length: 6 }, (_, i) => (
          <span key={i} className="nx-otp__cell" aria-hidden="true" data-active={focused && i === active ? '' : undefined}>
            {value[i] ?? ''}
          </span>
        ))}
        <input className="nx-otp__input" aria-labelledby={`${id}-l`} aria-describedby={`${id}-h`}
          inputMode="numeric" autoComplete="one-time-code" maxLength={6} autoFocus value={value}
          onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
          onChange={(e) => onChange(e.currentTarget.value.replace(/\D/g, '').slice(0, 6))} />
      </div>
    </fieldset>
  )
}
